export type UseCaseTemplateCategory =
  | 'Agent Workflows'
  | 'Campaign Execution'
  | 'Ads Audit & Optimization'
  | 'Competitive Intelligence'
  | 'SEO & Content'
  | 'Creative Generation'
  | 'Analytics & Reporting'
  | 'Automation & Monitoring'
  | 'Landing Pages & CRO'

export type UseCaseAccessMode = 'read-only' | 'review-gated-write'

export interface AgentWorkflowStep {
  title: string
  body: string
  command?: string
}

export interface AgentWorkflowDetails {
  outcome: string
  bestFor: string
  prerequisites: string[]
  agentPrompt: string
  steps: AgentWorkflowStep[]
  expectedOutputs: string[]
  accessMode: UseCaseAccessMode
  safety: string
  skillSlugs: string[]
}

export interface UseCaseTemplate {
  slug: string
  title: string
  /** What Soku does for this task — shown on cards and detail pages. */
  description: string
  /** The message you would actually send Soku to run this use case. */
  prompt: string
  category: UseCaseTemplateCategory
  /** Personas and platforms, used for search and card meta. */
  tags: string[]
  /** Products Soku touches while running this use case. */
  integrations: string[]
  iconEmoji: string
  /** Rich, quality-gated content for external-agent workflow pages. */
  agentWorkflow?: AgentWorkflowDetails
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const RETIRED_CLI_PATTERNS = [/soku auth login\s+--resource\b/i]
const LIVE_WRITE_REQUEST_PATTERN =
  /\b(?:apply|approve|change|create|delete|edit|execute|launch|modify|pause|publish|remove|resume|set|update|upload|write)\b[^.!?\n]{0,80}\b(?:account|ad|bid|budget|campaign|configuration|content|keyword|page|setting|site)\b/i
const READ_ONLY_SOKU_COMMAND_PATTERNS = [
  /^auth\s+login\b/i,
  /^ads\s+(?:--help|list-ad-accounts\b)/i,
  /^egress\s+providers\b/i,
  /^egress\s+--\s+curl\s+(?!.*(?:--data(?:-\w+)?|-d|--request|-X)\b)/i,
  /^ga4\s+(?:--help|list-properties\b|list-top-pages\b)/i,
  /^gsc\s+--help\b/i,
  /^memory\s+(?:list|search)\b/i,
  /^posthog\s+(?:list-projects|list-tools|query)\b/i,
  /^seo-hosting\s+status\b/i,
  /^skill\s+(?:install|list|list-installed|status)\b/i,
  /^workspace\s+(?:resolve|status|use-brand)\b/i,
]
const APPROVAL_BYPASS_PATTERN =
  /\b(?:(?:without|skip(?:ping)?|bypass(?:ing)?|no)\b[^.!?\n]{0,48}\b(?:human |user )?(?:approval|confirmation|review)\b|(?:approval|confirmation|review)\b[^.!?\n]{0,32}\b(?:is\s+)?(?:not required|optional|unnecessary)\b|(?:automatically|immediately|now|right away)\b[^.!?\n]{0,48}\b(?:approve|execute|publish|launch|apply|update|change|set)\b|(?:approve|execute|publish|launch|apply|update|change|set)\b[^.!?\n]{0,48}\b(?:automatically|immediately|now|right away)\b|(?:approve|execute|publish|launch|apply|update|change|set)\b[^.!?\n]{0,80}\b(?:account|ad|bid|budget|campaign|configuration|content|keyword|page|setting|site)?[^.!?\n]{0,32}\b(?:before|prior to)\b[^.!?\n]{0,32}\b(?:approval|confirmation|review)\b|(?:approval|confirmation|review)\b[^.!?\n]{0,32}\b(?:after|following)\b[^.!?\n]{0,32}\b(?:execution|publishing|launching|updating|changing)\b)/i

export const READ_ONLY_AGENT_BOUNDARY =
  'Safety boundary: do not change connected accounts or publish content. Use read commands only, and return proposed actions for a human to review separately.'

export const REVIEW_GATED_AGENT_BOUNDARY =
  'Safety boundary: stop before execution. Continue only after the user explicitly approves the exact review, revision, account, and destination described in the pending change.'

export function getAgentWorkflowPrompt(workflow: AgentWorkflowDetails): string {
  const boundary =
    workflow.accessMode === 'read-only'
      ? READ_ONLY_AGENT_BOUNDARY
      : REVIEW_GATED_AGENT_BOUNDARY
  return `${workflow.agentPrompt.trim()}\n\n${boundary}`
}

function hasLiveWriteRequest(prompt: string): boolean {
  const taskLanguage = prompt.replace(
    /\b(?:install or )?update Soku CLI\b/gi,
    '',
  )
  return LIVE_WRITE_REQUEST_PATTERN.test(taskLanguage)
}

function findUnsupportedReadOnlyCommand(
  steps: AgentWorkflowStep[],
): string | undefined {
  for (const step of steps) {
    for (const segment of (step.command ?? '').split(/&&|;|\n/)) {
      const sokuCommand = segment.match(/\bsoku\s+(.+)$/i)?.[1]?.trim()
      if (
        sokuCommand &&
        !READ_ONLY_SOKU_COMMAND_PATTERNS.some(pattern =>
          pattern.test(sokuCommand),
        )
      ) {
        return `soku ${sokuCommand}`
      }
    }
  }
  return undefined
}

function requireText(value: string, minimum: number, label: string): void {
  if (value.trim().length < minimum) {
    throw new Error(`${label} must be at least ${minimum} characters`)
  }
}

function requireUnique(value: string, seen: Set<string>, label: string): void {
  const normalized = value.trim().toLowerCase()
  if (seen.has(normalized)) {
    throw new Error(`Duplicate ${label}: ${value}`)
  }
  seen.add(normalized)
}

/**
 * Fail fast when a programmatic use-case batch would create duplicate, thin,
 * unsafe, or obsolete public pages. This runs for the exported corpus and is
 * also exercised directly with malformed fixtures in unit tests.
 */
export function validateUseCaseTemplates(entries: UseCaseTemplate[]): void {
  const slugs = new Set<string>()
  const titles = new Set<string>()

  for (const entry of entries) {
    if (!SLUG_PATTERN.test(entry.slug)) {
      throw new Error(`Invalid use-case slug: ${entry.slug}`)
    }
    requireUnique(entry.slug, slugs, 'use-case slug')
    requireUnique(entry.title, titles, 'use-case title')

    const workflow = entry.agentWorkflow
    if (!workflow) continue

    if (entry.category !== 'Agent Workflows') {
      throw new Error(
        `${entry.slug}: agent workflows must use Agent Workflows category`,
      )
    }

    requireText(entry.description, 90, `${entry.slug}.description`)
    requireText(entry.prompt, 80, `${entry.slug}.prompt`)
    requireText(workflow.outcome, 80, `${entry.slug}.outcome`)
    requireText(workflow.bestFor, 50, `${entry.slug}.bestFor`)
    requireText(workflow.agentPrompt, 140, `${entry.slug}.agentPrompt`)
    requireText(workflow.safety, 80, `${entry.slug}.safety`)

    if (workflow.prerequisites.length < 2) {
      throw new Error(`${entry.slug}: at least two prerequisites are required`)
    }
    if (
      workflow.steps.length < 3 ||
      !workflow.steps.some(step => step.command)
    ) {
      throw new Error(
        `${entry.slug}: at least three steps and one CLI command are required`,
      )
    }
    if (workflow.expectedOutputs.length < 2) {
      throw new Error(
        `${entry.slug}: at least two expected outputs are required`,
      )
    }
    if (workflow.skillSlugs.length === 0) {
      throw new Error(`${entry.slug}: at least one related skill is required`)
    }

    const commandText = JSON.stringify(entry)
    for (const pattern of RETIRED_CLI_PATTERNS) {
      if (pattern.test(commandText)) {
        throw new Error(`${entry.slug}: contains a retired CLI login command`)
      }
    }

    const approvalLanguage = JSON.stringify(entry)
    if (
      workflow.accessMode === 'review-gated-write' &&
      APPROVAL_BYPASS_PATTERN.test(approvalLanguage)
    ) {
      throw new Error(
        `${entry.slug}: write workflows must not contain approval bypass language`,
      )
    }
    if (workflow.accessMode === 'read-only') {
      if (hasLiveWriteRequest(workflow.agentPrompt)) {
        throw new Error(
          `${entry.slug}: read-only prompt appears to request a connected-account write`,
        )
      }
      const unsafeCommand = findUnsupportedReadOnlyCommand(workflow.steps)
      if (unsafeCommand) {
        throw new Error(
          `${entry.slug}: read-only workflow contains an unsupported Soku command: ${unsafeCommand}`,
        )
      }
    }
  }
}

export function validateUseCaseRouteSlugs(
  templates: Pick<UseCaseTemplate, 'slug'>[],
  caseStudies: { slug: string }[],
): void {
  const templateSlugs = new Set(templates.map(template => template.slug))
  for (const caseStudy of caseStudies) {
    if (templateSlugs.has(caseStudy.slug)) {
      throw new Error(
        `Duplicate public use-case route slug across template and case study: ${caseStudy.slug}`,
      )
    }
  }
}

export function validateUseCaseSkillSlugs(
  entries: UseCaseTemplate[],
  availableSkillSlugs: Iterable<string>,
): void {
  const available = new Set(availableSkillSlugs)
  for (const entry of entries) {
    const workflow = entry.agentWorkflow
    if (!workflow) continue

    const installedSlugs = workflow.steps.flatMap(step =>
      Array.from(
        step.command?.matchAll(/soku skill install\s+([^&;\n]+)/g) ?? [],
      ).flatMap(match =>
        match[1]
          .trim()
          .split(/\s+/)
          .filter(token => !token.startsWith('-') && !token.startsWith('<')),
      ),
    )
    for (const slug of [...workflow.skillSlugs, ...installedSlugs]) {
      if (!available.has(slug)) {
        throw new Error(`${entry.slug}: unknown Soku skill slug: ${slug}`)
      }
    }
  }
}
